package com.ijoin.update

import com.ijoin.BuildConfig
import com.ijoin.data.api.AppVersionApi
import com.ijoin.data.model.UpdateCheckResult
import com.ijoin.data.model.UpdateType
import com.ijoin.getPlatform
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

object AppUpdateChecker {

    private val _updateState = MutableStateFlow<UpdateCheckResult?>(null)
    val updateState: StateFlow<UpdateCheckResult?> = _updateState.asStateFlow()

    private val _isChecking = MutableStateFlow(false)
    val isChecking: StateFlow<Boolean> = _isChecking.asStateFlow()

    private var lastCheckTime: Long = 0
    private const val CHECK_INTERVAL_MS = 3600_000L // 1 hour

    suspend fun checkForUpdate(force: Boolean = false): UpdateCheckResult? {
        val platform = getPlatform()

        println("[AppUpdateChecker] checkForUpdate called, platform=${platform.name}, isWeb=${platform.isWeb}, isMobile=${platform.isMobile}")

        // Skip web platform
        if (platform.isWeb) {
            println("[AppUpdateChecker] Skipping - web platform")
            return null
        }

        // Throttle checks unless forced
        val now = currentTimeMillis()
        if (!force && (now - lastCheckTime) < CHECK_INTERVAL_MS) {
            println("[AppUpdateChecker] Throttled - returning cached value")
            return _updateState.value
        }

        _isChecking.value = true

        try {
            val platformName = when {
                platform.name.contains("Android", ignoreCase = true) -> "android"
                platform.name.contains("iOS", ignoreCase = true) -> "ios"
                else -> {
                    println("[AppUpdateChecker] Unknown platform: ${platform.name}")
                    return null
                }
            }

            val currentVersion = BuildConfig.VERSION_NAME
            println("[AppUpdateChecker] Checking version: platform=$platformName, currentVersion=$currentVersion")

            val result = AppVersionApi.checkVersion(platformName, currentVersion)

            return result.fold(
                onSuccess = { response ->
                    println("[AppUpdateChecker] Success: latestVersion=${response.latestVersion}, updateRequired=${response.updateRequired}, updateAvailable=${response.updateAvailable}")
                    val updateType = when {
                        response.updateRequired -> UpdateType.REQUIRED
                        response.updateAvailable -> UpdateType.OPTIONAL
                        else -> UpdateType.NONE
                    }

                    val checkResult = UpdateCheckResult(
                        updateType = updateType,
                        currentVersion = currentVersion,
                        latestVersion = response.latestVersion,
                        updateUrl = response.updateUrl,
                        releaseNotes = response.releaseNotes
                    )

                    _updateState.value = checkResult
                    lastCheckTime = now
                    checkResult
                },
                onFailure = { error ->
                    println("[AppUpdateChecker] Error: ${error.message}")
                    error.printStackTrace()
                    null
                }
            )
        } finally {
            _isChecking.value = false
        }
    }

    fun dismissUpdate() {
        val current = _updateState.value
        if (current?.updateType == UpdateType.OPTIONAL) {
            _updateState.value = null
        }
        // Required updates cannot be dismissed
    }

    fun clearState() {
        _updateState.value = null
        lastCheckTime = 0
    }
}

// Platform-specific time function
expect fun currentTimeMillis(): Long
